import {Box, Button, HStack, Text, VStack} from '@chakra-ui/react';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {useHalState} from '@synako/haligator';
import {
  isItemStateCatalogueFF,
  isItemStateCatalogueMarketplace,
  useLigneDePanierCommands,
} from 'ff-common';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire';
import {
  InnerVignetteArticleVStack,
  VignetteArticleVStack,
} from '@/domains/mediatheque/VignetteArticleVStack';
import {
  imageOptimiseeH,
  placeholderImage,
} from '@/domains/mediatheque/optimisation';
import {BuyProductPanierInput} from '@/domains/panier/BuyProduct/BuyProductPanierInput';
import {TrashIcon} from '@/components/svg/TrashIcon';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {ModaleErreurDisponibilite} from '@/domains/cadencier/components/ModaleErreurDisponibilite';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';
import {useLivraisonsPossiblesContext} from 'ff-common';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {MargesProduit} from '@/domains/ligneProduit/MargesProduit';
import {TagCadencier} from '@/domains/cadencier/components/TagCadencier';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';

// Composant affichant une ligne de panier dans le side panel panier
export const SidePanelLignePanier: React.FC<{
  ligneDePanier: State<LigneDePanierDesc>;
}> = ({ligneDePanier}) => {
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const {prixDraft, onEditionPrixChange} = useEditionPrixState();
  // Nécessaire pour que le changement de quantité marketplace rafraîchisse le bon panier dans le contexte
  const {setSelectedMarketplaceId} = useLivraisonsPossiblesContext();

  const item = useHalState(ligneDePanier.follow('item_2')); // item_2 est l'item catalogue unifié, il peut être FF ou marketplace selon le type de panier
  // On discrimine le type pour adapter l'affichage (image, contrôles quantité, couleur)
  const itemStateFF =
    item.state && isItemStateCatalogueFF(item.state) ? item.state : undefined;
  const itemStateMarketplace =
    item.state && isItemStateCatalogueMarketplace(item.state)
      ? item.state
      : undefined;
  const {couleur} = useMarketplaceBrandColors(
    itemStateMarketplace?.data.marketplace_id,
  );

  // Commandes de la ligne : supprimer, changer quantité, etc.
  // Pour marketplace, on rafraîchit le contexte après un changement de quantité
  const commands = useLigneDePanierCommands(
    ligneDePanier,
    textToast,
    onError,
    ModaleErreurDisponibilite,
    undefined,
    undefined,
    undefined,
    itemStateMarketplace
      ? {
          onChangerLaQuantiteSuccess: (event) => {
            // Quantité à zéro sur la dernière ligne : le serveur supprime la
            // ligne et le panier, on repasse alors sur FF.
            setSelectedMarketplaceId(
              event.data.data.panier.nombre_de_lignes > 0
                ? itemStateMarketplace.data.marketplace_id
                : null,
            );
          },
          onSupprimerSuccess: (event) => {
            // Dernière ligne supprimée : le serveur supprime le panier avec
            // elle, on repasse alors sur FF.
            if (event.data.data.panier.nombre_de_lignes <= 0) {
              setSelectedMarketplaceId(null);
            }
          },
        }
      : undefined,
  );
  const {somethingSubmitting, supprimer, changerLaQuantite} = commands;

  const hasArticleLink = ligneDePanier.hasOneLink('article');
  const articleNav = hasArticleLink
    ? ligneDePanier.follow('article')
    : undefined;
  const articleState = useHalState(articleNav);

  const toledeData = isItemStateCatalogueFF(item.state)
    ? item.state.data.tolede
    : undefined;

  const uniteFacturation = ligneDePanier.data.unite_de_facturation;
  const prixUnitaire = ligneDePanier.data.prix_unitaire;

  // Récupération des tags de prix pour l'affichage du prix unitaire
  const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
    ligneDePanier,
    itemStateFF?.data.tolede.prix_par_livraison?.[0]?.categorie,
    {contexte: 'panier'},
  );

  return (
    <VStack
      p='3'
      borderBottom='1px solid'
      borderColor='gray.light'
      gap='2'
      bg='white'
      alignItems='stretch'
    >
      {/* Ligne 1 : Image + Infos + Poubelle */}
      <HStack
        alignItems='start'
        gap='3'
      >
        {/* Image produit */}
        <Box flexShrink={0}>
          {/* FF : image via le lien article / Marketplace : image depuis item_2 */}
          {hasArticleLink ? (
            <VignetteArticleVStack
              article={articleNav}
              boxSize='60px'
            />
          ) : (
            <InnerVignetteArticleVStack
              imageUrl={
                itemStateMarketplace?.data.image_principale_url
                  ? imageOptimiseeH(
                      itemStateMarketplace.data.image_principale_url,
                      120,
                    )
                  : placeholderImage
              }
              boxSize='60px'
            />
          )}
        </Box>

        {/* Infos produit */}
        <VStack
          flex='1'
          alignItems='start'
          gap='0.5'
          minW='0'
        >
          {/* Codes article + marges (commercial uniquement, FF only) */}
          <HStack
            alignItems='center'
            gap='2'
            flexWrap='wrap'
          >
            <ArticleCodesDisplay
              code_article={ligneDePanier.data.code_article}
              toledeData={toledeData}
            />
            <MargesProduit
              code_article={ligneDePanier.data.code_article}
              ligneDePanier={itemStateFF ? ligneDePanier : undefined}
              isItemCatalogueFF={!!itemStateFF}
              prixDraft={prixDraft}
            />
            {itemStateFF && (
              <TagCadencier code_article={ligneDePanier.data.code_article} />
            )}
          </HStack>

          {/* Libellé */}
          <Text
            fontWeight='600'
            fontSize='sm'
            lineHeight='short'
            noOfLines={2}
          >
            {articleState.data?.tolede.libelle_commercial ??
              ligneDePanier.data.libelle}
          </Text>

          {/* Marque */}
          {articleState.data?.tolede.marque?.[0] && (
            <Text
              fontSize='xs'
              color='gray.dark'
              noOfLines={1}
            >
              {articleState.data.tolede.marque[0]}
            </Text>
          )}
        </VStack>

        {/* Bouton suppression */}
        <Button
          variant='ghost'
          size='sm'
          p='1'
          minW='auto'
          flexShrink={0}
          onClick={() => {
            supprimer.submit({});
          }}
        >
          <TrashIcon height='5' />
        </Button>
      </HStack>

      {/* Ligne 3 : Boutons +/- quantité + Prix */}
      <HStack
        justifyContent='space-between'
        alignItems='center'
        gap='3'
      >
        <Box
          flex='1'
          maxW='220px'
        >
          {/* Boutons - / quantité / + : marketplace toujours en unitaire, FF selon l'unité choisie */}
          {itemStateMarketplace ? (
            <BuyProductPanierInput
              itemCatalogue={itemStateMarketplace}
              quantite={ligneDePanier.data.quantite}
              commande_en='unitaire'
              somethingSubmitting={somethingSubmitting}
              context='panier'
              primaryColor={couleur}
              displayBg='gray.lighter'
              onQuantite={(q) => {
                changerLaQuantite.submit({quantite: q});
              }}
            />
          ) : (
            itemStateFF && (
              <BuyProductPanierInput
                itemCatalogue={itemStateFF}
                quantite={ligneDePanier.data.quantite}
                commande_en={ligneDePanier.data.commande_en ?? 'unitaire'}
                somethingSubmitting={somethingSubmitting}
                context='panier'
                primaryColor='blue.main_light_bg'
                secondaryColor='blue.main'
                displayBg='gray.lighter'
                onQuantite={(q) => {
                  changerLaQuantite.submit({quantite: q});
                }}
              />
            )
          )}
        </Box>

        {/* Prix unitaire — réutilise le composant du cadencier
            En FF, on passe `ligneDePanier` pour activer l'édition commerciale. */}
        <PrixBlockUnitaire
          prixUnitaire={prixUnitaire}
          // TTC de la ligne seulement : celui du catalogue peut différer du prix du panier
          prixUnitaireTtc={ligneDePanier.data.prix_unitaire_ttc}
          uniteFacturation={uniteFacturation}
          tauxTVA={itemStateFF?.data.tva ?? itemStateMarketplace?.data.tva}
          categorieDeTarif={categorieDeTarif}
          modeTarifaireSouhaite={modeTarifaireSouhaite}
          fontSizeMain='lg'
          fontSizeSecondary='xs'
          style={{minW: 'auto', mt: '0', alignItems: 'end'}}
          ligneDePanier={itemStateFF ? ligneDePanier : undefined}
          contexte='panier'
          onEditionPrixChange={onEditionPrixChange}
        />
      </HStack>
    </VStack>
  );
};
